import { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormik } from 'formik';
import { useIntl } from 'react-intl';
import { array, number, object, string } from 'yup';

import map from 'lodash/map';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';

import Grid from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

import { locale } from 'src/config';
import {
  useCreateStandbyTransfer,
  useGetTargetShiftOptions,
} from 'src/pages/ProjectDetailsPage/api';
import TextInput from 'src/components/TextInput';
import ConfirmModal from 'src/components/ConfirmModal';
import { checkNumberTyping } from 'src/utils/standards';

import messages from './messages';
import ShiftsTable from './components/ShiftsTable';
import StandbyTable from './components/StandbyTable';

const checkParam = { locale };

interface TransferFormProps {
  standbyJobUuid: string;
  selectedUsers: Array<UCM.ApplicationType>;
  onUserUnselect: (user: UCM.ApplicationType) => void;
}

export default function TransferForm({
  onUserUnselect,
  standbyJobUuid,
  selectedUsers,
}: TransferFormProps) {
  const { formatMessage } = useIntl();

  const [debouncedJobId, setDebouncedJobId] = useState('');
  const [validateOnChange, setValidateOnChange] = useState(false);

  const { mutate: createStandbyTransfer, isPending: isTransfering } =
    useCreateStandbyTransfer();
  const { data: targetShiftOptions, isFetching: isFetchingShiftOptions } =
    useGetTargetShiftOptions(
      {
        standbyJobUuid,
        jobId: debouncedJobId,
        selectedUserUuids: map(selectedUsers, 'uuid'),
      },
      { enabled: Boolean(debouncedJobId) },
    );

  const initialValues = {
    jobId: '',
    jobUuid: '',
    jobTitle: '',
    projectId: '',
    shiftUuids: [],
    projectName: '',
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      jobTitle: string(),
      projectId: string(),
      projectName: string(),
      jobId: number().required().min(1),
      shiftUuids: array()
        .of(string())
        .min(1, formatMessage(messages.atLeastOneShiftRequired)),
    }),
    onSubmit: () => {},
  });

  const notTransferrable = useMemo(
    () =>
      targetShiftOptions?.transferable === false && !targetShiftOptions?.isFlex,
    [targetShiftOptions],
  );

  const { values, errors } = formik;

  useEffect(() => {
    if (!debouncedJobId) {
      formik.resetForm();
      return;
    }

    if (isFetchingShiftOptions) return;

    if (targetShiftOptions?.shifts.length) {
      formik.setFieldValue(
        'shiftUuids',
        map(filter(targetShiftOptions?.shifts, { selected: true }), 'uuid'),
      );
    }

    const jobNotFound = formatMessage(messages.targetJobNotFound);

    const fields = {
      jobUuid: targetShiftOptions?.uuid ?? jobNotFound,
      jobTitle: targetShiftOptions?.title ?? jobNotFound,
      projectId: targetShiftOptions?.project.id ?? jobNotFound,
      projectName: targetShiftOptions?.project.name ?? jobNotFound,
    };

    formik.setFieldValue('jobUuid', fields.jobUuid);
    formik.setFieldValue('jobTitle', fields.jobTitle);
    formik.setFieldValue('projectId', fields.projectId);
    formik.setFieldValue('projectName', fields.projectName);
  }, [targetShiftOptions, debouncedJobId, isFetchingShiftOptions]);

  const debouncedSetJobId = useCallback(
    debounce((value: string) => {
      setDebouncedJobId(value);
    }, 1000),
    [],
  );

  const handleNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (checkNumberTyping(value, checkParam)) {
        formik.setFieldValue(name, value);
        debouncedSetJobId(value);
      }
    },
    [],
  );

  const handleClose = useCallback(() => {
    setValidateOnChange(false);
    setDebouncedJobId('');
    formik.resetForm();
  }, []);

  const handleCheck = useCallback(
    (shiftUuids: Array<string>) =>
      formik.setFieldValue('shiftUuids', shiftUuids),
    [],
  );

  const handleSubmit = useCallback(async () => {
    setValidateOnChange(true);

    const errors = await formik.validateForm();
    const isValid = isEmpty(errors);

    if (!isValid) return false;

    return new Promise<boolean>((resolve) => {
      createStandbyTransfer(
        {
          jobUuid: values.jobUuid,
          shiftUuids: values.shiftUuids,
          userUuids: map(selectedUsers, 'uuid'),
        },
        {
          onSuccess: () => {
            resolve(true);
          },
          onError: () => {
            resolve(false);
          },
        },
      );
    });
  }, [values, selectedUsers]);

  return (
    <ConfirmModal
      width="700px"
      onClose={handleClose}
      onConfirm={handleSubmit}
      title={formatMessage(messages.title)}
      loading={isTransfering || isFetchingShiftOptions}
      disabled={!targetShiftOptions?.shifts.length}
      submitTextButton={formatMessage(messages.confirmLabel)}
      triggerButton={(handleOpen) => (
        <Button
          variant="contained"
          onClick={handleOpen}
          disabled={isEmpty(selectedUsers)}
        >
          {formatMessage(messages.transferButton)}
        </Button>
      )}
    >
      <FormControl fullWidth error={false}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              fullWidth
              autoFocus
              name="jobId"
              type="search"
              error={!!errors.jobId}
              helperText={errors.jobId}
              value={values.jobId || ''}
              onChange={handleNumberChange}
              label={formatMessage(messages.jobIdLabel)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <TextInput
              disabled
              fullWidth
              multiline
              type="search"
              name="jobName"
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
              value={values.jobTitle || ''}
              label={formatMessage(messages.jobTitleLabel)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextInput
              disabled
              fullWidth
              type="search"
              name="projectId"
              error={!!errors.projectId}
              helperText={errors.projectId}
              value={values.projectId || ''}
              label={formatMessage(messages.projectIdLabel)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <TextInput
              disabled
              fullWidth
              multiline
              type="search"
              name="projectName"
              error={!!errors.projectName}
              helperText={errors.projectName}
              value={values.projectName || ''}
              label={formatMessage(messages.projectNameLabel)}
            />
          </Grid>

          <Grid size={12}>
            <ShiftsTable
              onCheck={handleCheck}
              checked={values.shiftUuids}
              error={!!errors.shiftUuids}
              shifts={targetShiftOptions?.shifts}
              helperText={errors.shiftUuids as string}
            />
          </Grid>

          <Grid size={12}>
            <StandbyTable users={selectedUsers} onUnselect={onUserUnselect} />
          </Grid>
        </Grid>

        {notTransferrable && (
          <Grid size={12}>
            <FormHelperText>
              {formatMessage(messages.transferableMessage)}
            </FormHelperText>
          </Grid>
        )}
      </FormControl>
    </ConfirmModal>
  );
}
